import { useState } from "react";
import { useSelector } from "react-redux";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  TextField,
  Button,
  IconButton,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";

const lightThemeColors = {
  primary: "#4361ee",
  secondary: "#3a0ca3",
  success: "#2ecc71",
  warning: "#f39c12",
  error: "#e74c3c",
  info: "#3498db",
  background: "#f8f9fd",
  card: "#ffffff",
  text: "#2d3748",
  textSecondary: "#718096",
  columnHeader: "#ebf3ff",
  columnBackground: "#ffffff",
  columnBackgroundHover: "#f7faff",
  taskBorder: "#e2e8f0",
  taskShadow: "0 4px 6px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.08)",
  taskShadowHover: "0 10px 15px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.08)",
  headerBackground: "#ffffff",
  headerShadow: "0 1px 3px rgba(0,0,0,0.05)",
};

const darkThemeColors = {
  primary: "#4361ee",
  secondary: "#7b5cff",
  success: "#2ecc71",
  warning: "#f39c12",
  error: "#e74c3c",
  info: "#3498db",
  background: "#1a202c",
  card: "#2d3748",
  text: "#f7fafc",
  textSecondary: "#a0aec0",
  columnHeader: "#2d3748",
  columnBackground: "#2d3748",
  columnBackgroundHover: "#3a4556",
  taskBorder: "#4a5568",
  taskShadow: "0 4px 6px rgba(0,0,0,0.2), 0 1px 3px rgba(0,0,0,0.4)",
  taskShadowHover: "0 10px 15px rgba(0,0,0,0.2), 0 4px 6px rgba(0,0,0,0.3)",
  headerBackground: "#2d3748",
  headerShadow: "0 1px 3px rgba(0,0,0,0.3)",
};

export default function AnrComments({
  comments,
  onAddComment,
  onEditComment,
  onDeleteComment,
  isDarkMode = false,
}) {
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");

  const userState = useSelector((state) => state.user);
  const loggedInUserId = userState?.user?._id;
  console.log("Logged in user ID:", loggedInUserId);

  const themeColors = isDarkMode ? darkThemeColors : lightThemeColors;

  const handleAdd = () => {
    if (!newComment.trim()) return;
    onAddComment(newComment.trim());
    setNewComment("");
  };

  const handleSaveEdit = () => {
    if (!editCommentText.trim()) return;
    onEditComment(editingCommentId, editCommentText.trim());
    setEditingCommentId(null);
    setEditCommentText("");
  };

  return (
    <Grid item xs={12}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: isDarkMode ? "rgba(52, 152, 219, 0.1)" : "#f0f8ff",
          borderColor: isDarkMode ? "rgba(52, 152, 219, 0.3)" : "#3498db",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <ChatBubbleOutlineIcon sx={{ color: themeColors.info, mr: 1.5 }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Kommentare
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            label={`${comments.length || 0} Kommentar(e)`}
            size="small"
            variant="outlined"
            color="info"
          />
        </Box>

        {/* Comments List */}
        <List disablePadding>
          {comments.length > 0 ? (
            comments.map((comment) => (
              <ListItem
                key={comment._id}
                alignItems="flex-start"
                disablePadding
                sx={{
                  mb: 1,
                  pb: 1,
                  borderBottom: `1px solid ${
                    isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"
                  }`,
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor:
                        comment.authorModel === "Admin"
                          ? themeColors.primary
                          : themeColors.secondary,
                      width: 36,
                      height: 36,
                    }}
                  >
                    {comment.authorName?.charAt(0).toUpperCase() || "U"}
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={themeColors.text}
                      >
                        {comment.authorName || "Unbekannt"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={themeColors.textSecondary}
                      >
                        {new Date(comment.createdAt).toLocaleString("de-DE", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    editingCommentId === comment._id ? (
                      <Box mt={1}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          variant="outlined"
                          value={editCommentText}
                          onChange={(e) => setEditCommentText(e.target.value)}
                          InputProps={{ sx: { borderRadius: 2 } }}
                          size="small"
                        />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            mt: 1,
                          }}
                        >
                          <Button
                            size="small"
                            onClick={() => {
                              setEditingCommentId(null);
                              setEditCommentText("");
                            }}
                            sx={{ mr: 1 }}
                          >
                            Abbrechen
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={handleSaveEdit}
                          >
                            Speichern
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        component="div"
                        color={themeColors.text}
                        sx={{
                          mt: 1,
                          whiteSpace: "pre-wrap",
                          backgroundColor: isDarkMode
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(0,0,0,0.02)",
                          p: 1.5,
                          borderRadius: 2,
                        }}
                      >
                        {comment.comment}
                      </Typography>
                    )
                  }
                />

                {/* Comment Actions */}
                {editingCommentId !== comment._id &&
                  comment.userId === loggedInUserId && (
                    <Box sx={{ display: "flex", ml: 2, alignItems: "center" }}>
                      <Tooltip title="Bearbeiten">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingCommentId(comment._id);
                            setEditCommentText(comment.comment);
                          }}
                          sx={{
                            color: themeColors.primary,
                            opacity: 0.7,
                            "&:hover": { opacity: 1 },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Löschen">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (
                              window.confirm(
                                "Möchten Sie diesen Kommentar wirklich löschen?"
                              )
                            ) {
                              onDeleteComment(comment._id);
                            }
                          }}
                          sx={{
                            color: themeColors.error,
                            opacity: 0.7,
                            "&:hover": { opacity: 1 },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
              </ListItem>
            ))
          ) : (
            <Box
              sx={{
                py: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                color: themeColors.textSecondary,
                backgroundColor: isDarkMode
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.02)",
                borderRadius: 2,
              }}
            >
              <ChatBubbleOutlineIcon
                sx={{ fontSize: "2rem", mb: 1, opacity: 0.6 }}
              />
              <Typography variant="body2">
                Keine Kommentare vorhanden
              </Typography>
            </Box>
          )}
        </List>

        {/* Add New Comment */}
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label="Neuen Kommentar hinzufügen"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Geben Sie Ihren Kommentar ein..."
            InputProps={{
              sx: { borderRadius: 2 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    color="primary"
                    onClick={handleAdd}
                    disabled={!newComment.trim()}
                    sx={{ mr: -1 }}
                  >
                    <SendIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>
    </Grid>
  );
}
